import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import {
  Alert, Button, Chip, Field, Modal, Note, PageHeader, Panel, Spinner, Table,
} from '../ui';

/**
 * A-34 — languages, and the strings behind them.
 *
 * This screen is in the admin console, so it is in English like the rest of it.
 * That is not an oversight: the people translating the customer portal are
 * Miraki's staff, and a translation tool that is itself half-translated is a
 * worse tool.
 *
 * The division it makes visible: an administrator can add a language and type
 * every string in it, and cannot invent a key. Keys are `packages/i18n`, because
 * a key is a promise that some component renders it; values are rows, because
 * they are content that will be revised for years without a release.
 */

interface LocaleRow {
  code: string;
  name: string;
  endonym: string;
  direction: 'ltr' | 'rtl';
  isActive: boolean;
  sortOrder: number;
  translated: number;
  total: number;
}

const useLocales = () =>
  useQuery({ queryKey: ['locales'], queryFn: () => api.get<LocaleRow[]>('/admin/locales') });

function useAction() {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const run = (fn: () => Promise<unknown>, after?: () => void): void => {
    setError(null);
    setBusy(true);
    void (async () => {
      try { await fn(); after?.(); }
      catch (e) { setError(e instanceof ApiError ? e.message : 'That did not work'); }
      finally { setBusy(false); }
    })();
  };
  return { error, busy, run, clear: () => setError(null) };
}

export function Languages() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useLocales();
  const act = useAction();
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const remove = useMutation({
    mutationFn: (code: string) => api.del<unknown>(`/admin/locales/${code}`),
    onSuccess: () => {
      setConfirmDelete(null);
      void qc.invalidateQueries({ queryKey: ['locales'] });
    },
  });

  const setActive = useMutation({
    mutationFn: ({ code, isActive }: { code: string; isActive: boolean }) =>
      api.put<unknown>(`/admin/locales/${code}`, { isActive }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['locales'] }); },
  });

  if (isLoading) return <Spinner />;
  if (error) return <Alert tone="stop">{(error).message}</Alert>;

  return (
    <>
      <PageHeader
        title="Languages"
        actions={<Button onClick={() => { act.clear(); setCreating(true); }}>New language</Button>}
      />
      {act.error ? <Alert tone="stop">{act.error}</Alert> : null}

      <Alert tone="them">
        <b>English is not in this list, and cannot be.</b> It is the source text: it ships in the
        application, it is what every untranslated key falls back to, and that fallback is what
        makes adding a language safe. A new language starts inactive and shows nobody anything
        until it is published here.
      </Alert>

      <Panel>
        <Table<LocaleRow>
          rows={data ?? []}
          rowKey={(l) => l.code}
          empty={<Note>No languages yet. The portal is English only.</Note>}
          columns={[
            {
              header: 'Language',
              render: (l) => (
                <>
                  <b>{l.name}</b> <span className="mk-dim">{l.code}</span>
                  {' '}<span lang={l.code}>{l.endonym}</span>
                </>
              ),
            },
            {
              header: 'Direction',
              render: (l) => (
                <Chip tone={l.direction === 'rtl' ? 'run' : 'us'}>{l.direction.toUpperCase()}</Chip>
              ),
            },
            {
              header: 'Translated', numeric: true,
              render: (l) => (
                <>
                  {l.translated} / {l.total}
                  {l.translated < l.total
                    ? <span className="mk-hint"> {Math.round((l.translated / l.total) * 100)}%</span>
                    : null}
                </>
              ),
            },
            {
              header: 'State',
              render: (l) => (
                <Chip tone={l.isActive ? 'done' : 'lapse'}>{l.isActive ? 'Published' : 'Draft'}</Chip>
              ),
            },
            {
              header: '', numeric: true,
              render: (l) => (
                <div style={{ display: 'flex', gap: 'var(--mk-space-3)', justifyContent: 'flex-end' }}>
                  {/* Bulk work belongs on a page of its own (A-35), not in a
                      dialog that takes this list away while you are in it. */}
                  <Link className="mk-btn mk-btn-g mk-btn-s" to={`/translations?lang=${l.code}`}>
                    Translate
                  </Link>
                  <Button
                    size="sm" tone="ghost" disabled={act.busy}
                    onClick={() => act.run(() => setActive.mutateAsync({ code: l.code, isActive: !l.isActive }))}
                  >
                    {l.isActive ? 'Unpublish' : 'Publish'}
                  </Button>
                  {/* Only once it is unpublished. The service refuses either way;
                      disabling here means the button is not offering an action
                      that would come back 409. */}
                  <Button
                    size="sm" tone="ghost"
                    disabled={act.busy || l.isActive}
                    {...(l.isActive ? { title: 'Unpublish it first' } : {})}
                    onClick={() => {
                      if (confirmDelete === l.code) act.run(() => remove.mutateAsync(l.code));
                      else setConfirmDelete(l.code);
                    }}
                  >
                    {confirmDelete === l.code ? 'Confirm' : 'Delete'}
                  </Button>
                </div>
              ),
            },
          ]}
        />
      </Panel>

      {creating ? <NewLanguage onDone={() => setCreating(false)} /> : null}
    </>
  );
}

function NewLanguage({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const act = useAction();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [endonym, setEndonym] = useState('');

  const create = useMutation({
    mutationFn: (body: unknown) => api.post<unknown>('/admin/locales', body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['locales'] }); },
  });

  return (
    <Modal
      title="New language"
      onClose={onDone}
      footer={
        <>
          <Button
            disabled={act.busy || code.length < 2 || name.length < 2 || endonym.length < 1}
            onClick={() => act.run(() => create.mutateAsync({ code, name, endonym }), onDone)}
          >
            Add language
          </Button>
          <Button tone="ghost" onClick={onDone}>Cancel</Button>
        </>
      }
    >
      {act.error ? <Alert tone="stop">{act.error}</Alert> : null}
      <div className="mk-fset">
        <Field label="Code" hint="A BCP-47 tag — ar, fr, pt-BR. It becomes the page's lang attribute.">
          <input value={code} onChange={(e) => setCode(e.target.value.trim())} placeholder="fr" />
        </Field>
        <Field label="Name" hint="In English, for this list.">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="French" />
        </Field>
        <Field label="Endonym" hint="The language's name in itself. This is what customers see.">
          <input value={endonym} onChange={(e) => setEndonym(e.target.value)} placeholder="Français" lang={code || undefined} />
        </Field>
      </div>
      <Note>
        Text direction is not asked for — it is derived from the code, because there is one right
        answer per language and the wrong one produces a page nobody can read. The language is
        created unpublished; nothing changes for customers until you publish it.
      </Note>
    </Modal>
  );
}
